import { useCallback, useEffect, useRef } from 'react'

import { type PageTrackingConfig } from '../types/config'
import { type GroupContext, type UserContext } from '../types/context'

import { enrichEventProperties } from '../utils/enrichment'

export function usePageTracking(
  appInsights: any,
  isEnabled: boolean,
  isDebugMode: boolean,
  userContext: UserContext,
  groupContext: GroupContext,
  pageTrackingConfig: PageTrackingConfig,
  sessionId: React.MutableRefObject<string>
) {
  const currentPageRef = useRef<string | null>(null)

  const trackPageView = useCallback(
    (pageName?: string, properties: Record<string, any> = {}) => {
      if (!isEnabled || currentPageRef.current === pageName) return

      currentPageRef.current = pageName ?? window.location.pathname

      const pageViewProps = enrichEventProperties(
        {
          ...properties,
          sessionId: sessionId.current
        },
        userContext,
        groupContext
      )

      if (pageName) {
        pageViewProps.pageName = pageName
      }

      appInsights.trackPageView({ name: pageName, properties: pageViewProps })

      if (isDebugMode) {
        console.group('🔍 App Insights: Page View')
        console.log('Page:', pageName || window.location.pathname)
        console.log('Properties:', pageViewProps)
        console.groupEnd()
      }
    },
    [isEnabled, isDebugMode, userContext, groupContext, appInsights, sessionId]
  )

  // Track page changes
  useEffect(() => {
    if (!isEnabled) return

    // Track initial page view
    const trackCurrentPage = () => {
      const pathname = window.location.pathname
      const hash = pageTrackingConfig.includeHash ? window.location.hash : ''
      const pageName = pageTrackingConfig.enableAutoPageNameCapture ? pathname + hash : undefined

      const properties = {
        path: pathname + hash,
        url: window.location.href,
        referrer: document.referrer || '',
        sessionId: sessionId.current
      }

      trackPageView(pageName, properties)
    }

    // Track the initial page load
    trackCurrentPage()

    // Function to handle navigation events
    const handleNavigation = () => {
      trackCurrentPage()
    }

    // Listen for popstate (browser back/forward buttons)
    window.addEventListener('popstate', handleNavigation)

    // For programmatic navigation (like React Router)
    // We can use a MutationObserver to detect DOM changes that might indicate navigation
    const observer = new MutationObserver(() => {
      // Check if URL has changed since last check
      const currentUrl = window.location.href
      if (currentUrl !== lastUrlRef.current) {
        lastUrlRef.current = currentUrl
        handleNavigation()
      }
    })

    // Reference to store the last URL to detect changes
    const lastUrlRef = { current: window.location.href }

    // Observe the document for changes
    observer.observe(document, { subtree: true, childList: true })

    // Clean up event listeners when component unmounts
    return () => {
      window.removeEventListener('popstate', handleNavigation)
      observer.disconnect()
    }
  }, [isEnabled, pageTrackingConfig, trackPageView, sessionId])

  return {
    trackPageView,
    currentPage: currentPageRef
  }
}
